import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Loader2, ChevronDown, Plus, Trash2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ctc_supplier_token';
const authApi = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const SECTION_META = {
  company: { title: 'Company details', hint: 'Confirm your legal entity and website.' },
  trading: { title: 'Trading status', hint: 'Confirm you are actively trading.' },
  portfolio: { title: 'Product portfolio', hint: 'Confirm which products to keep, remove or add.' },
  contacts: { title: 'Key contacts', hint: 'Who should we deal with internally?' },
};
const TRADING_OPTIONS = ['Actively trading', 'Recently launched', 'Dormant', 'Ceased trading', 'In administration'];
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };
const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold text-gray-500">{label}</span>
    <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-[#00A79D] focus:ring-1 focus:ring-[#00A79D] outline-none" />
  </label>
);

export const SupplierDataCheck = ({ status, onChange }) => {
  const [open, setOpen] = useState(() => {
    const first = (status.section_order || []).find((k) => !status.sections[k].confirmed);
    return first || 'company';
  });
  const [drafts, setDrafts] = useState(() => {
    const d = {};
    for (const k of status.section_order) d[k] = JSON.parse(JSON.stringify(status.sections[k].data || {}));
    return d;
  });
  const [busy, setBusy] = useState('');

  const setDraft = (k, patch) => setDrafts((p) => ({ ...p, [k]: { ...p[k], ...patch } }));

  const confirm = async (k) => {
    setBusy(k);
    try {
      const { data } = await authApi().post(`/supplier-verify/section/${k}`, { data: drafts[k] });
      onChange(data);
      toast.success(`${SECTION_META[k].title} confirmed`);
      const next = (data.section_order || []).find((x) => !data.sections[x].confirmed);
      if (next) setOpen(next);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not confirm'); }
    finally { setBusy(''); }
  };

  const confirmedCount = status.section_order.filter((k) => status.sections[k].confirmed).length;

  return (
    <div data-testid="supplier-data-check">
      <div className="bg-gradient-to-br from-[#0f2438] to-[#1A3A5F] text-white rounded-2xl p-7 mb-6">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#7FE3DB]"><ShieldCheck className="w-3.5 h-3.5" /> Supplier data check</span>
        <h1 className="font-heading text-2xl font-bold mt-2">Confirm your core details</h1>
        <p className="text-sm text-[#C6D5E3] mt-1.5">Verify these four sections to unlock your full supplier portal and your subscription quote.</p>
        <div className="flex gap-1.5 mt-4">
          {status.section_order.map((k) => (
            <span key={k} className={`h-1.5 flex-1 rounded-full ${status.sections[k].confirmed ? 'bg-[#00AEEF]' : 'bg-white/20'}`} />
          ))}
        </div>
        <p className="text-xs text-[#AEC2D6] mt-2" data-testid="data-check-progress">{confirmedCount} of {status.section_order.length} confirmed</p>
      </div>

      <div className="space-y-3">
        {status.section_order.map((k) => {
          const sec = status.sections[k];
          const meta = SECTION_META[k];
          const d = drafts[k];
          const isOpen = open === k;
          return (
            <div key={k} className="border border-gray-200 rounded-xl bg-white overflow-hidden" data-testid={`data-check-section-${k}`}>
              <button onClick={() => setOpen(isOpen ? '' : k)} className="w-full flex items-center justify-between px-5 py-4 text-left">
                <span className="flex items-center gap-3">
                  {sec.confirmed ? <CheckCircle2 className="w-5 h-5 text-[#00A79D]" data-testid={`data-check-confirmed-${k}`} /> : <Circle className="w-5 h-5 text-gray-300" />}
                  <span>
                    <span className="font-heading font-semibold text-gray-900 block">{meta.title}</span>
                    <span className="text-xs text-gray-500">{sec.confirmed ? `Confirmed ${fmt(sec.confirmed_at)}` : meta.hint}</span>
                  </span>
                </span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-3">
                  {k === 'company' && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Legal / registered name" value={d.legal_name} onChange={(v) => setDraft(k, { legal_name: v })} testid="dc-legal-name" />
                      <Field label="Companies House reg no." value={d.reg_no} onChange={(v) => setDraft(k, { reg_no: v })} testid="dc-reg-no" />
                      <Field label="Website" value={d.website} onChange={(v) => setDraft(k, { website: v })} placeholder="https://" testid="dc-website" />
                      <Field label="Registered address" value={d.address} onChange={(v) => setDraft(k, { address: v })} testid="dc-address" />
                    </div>
                  )}

                  {k === 'trading' && (
                    <label className="block">
                      <span className="text-xs font-semibold text-gray-500">Current trading status</span>
                      <select value={d.status || ''} onChange={(e) => setDraft(k, { status: e.target.value })} data-testid="dc-trading-status"
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                        <option value="">Select…</option>
                        {TRADING_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </label>
                  )}

                  {k === 'portfolio' && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">These are your current CTG listings. Mark any you want to remove, and request new ones below.</p>
                      {(d.items || []).map((it, i) => (
                        <div key={i} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2" data-testid={`dc-portfolio-item-${i}`}>
                          <span className="text-sm font-medium text-gray-800">{it.name}</span>
                          <div className="flex gap-1 text-xs">
                            {['keep', 'remove'].map((dec) => (
                              <button key={dec} onClick={() => { const items = [...d.items]; items[i] = { ...it, decision: dec }; setDraft(k, { items }); }}
                                data-testid={`dc-portfolio-${i}-${dec}`}
                                className={`px-3 py-1 rounded-full font-semibold ${it.decision === dec ? (dec === 'keep' ? 'bg-[#00A79D] text-white' : 'bg-red-500 text-white') : 'bg-gray-100 text-gray-600'}`}>
                                {dec === 'keep' ? 'Keep' : 'Remove'}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div className="pt-1">
                        <span className="text-xs font-semibold text-gray-500">Request to add a product (one per line)</span>
                        {(d.add_requests || []).map((r, i) => (
                          <div key={i} className="flex gap-2 mt-1">
                            <input value={r} onChange={(e) => { const a = [...d.add_requests]; a[i] = e.target.value; setDraft(k, { add_requests: a }); }}
                              data-testid={`dc-add-request-${i}`} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="New product name" />
                            <button onClick={() => setDraft(k, { add_requests: d.add_requests.filter((_, x) => x !== i) })} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button onClick={() => setDraft(k, { add_requests: [...(d.add_requests || []), ''] })} data-testid="dc-add-request-btn"
                          className="mt-2 inline-flex items-center gap-1 text-sm text-[#00857D] font-semibold"><Plus className="w-4 h-4" /> Add a product request</button>
                      </div>
                    </div>
                  )}

                  {k === 'contacts' && (
                    <div className="space-y-2">
                      {(d.contacts || []).map((c, i) => (
                        <div key={i} className="grid sm:grid-cols-4 gap-2 items-end border border-gray-200 rounded-lg p-3" data-testid={`dc-contact-${i}`}>
                          <Field label="Name" value={c.name} onChange={(v) => { const a = [...d.contacts]; a[i] = { ...c, name: v }; setDraft(k, { contacts: a }); }} testid={`dc-contact-${i}-name`} />
                          <Field label="Role" value={c.role} onChange={(v) => { const a = [...d.contacts]; a[i] = { ...c, role: v }; setDraft(k, { contacts: a }); }} />
                          <Field label="Email" value={c.email} onChange={(v) => { const a = [...d.contacts]; a[i] = { ...c, email: v }; setDraft(k, { contacts: a }); }} />
                          <div className="flex gap-2 items-end">
                            <Field label="Phone" value={c.phone} onChange={(v) => { const a = [...d.contacts]; a[i] = { ...c, phone: v }; setDraft(k, { contacts: a }); }} />
                            <button onClick={() => setDraft(k, { contacts: d.contacts.filter((_, x) => x !== i) })} className="text-gray-400 hover:text-red-500 pb-2"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => setDraft(k, { contacts: [...(d.contacts || []), {}] })} data-testid="dc-add-contact-btn"
                        className="inline-flex items-center gap-1 text-sm text-[#00857D] font-semibold"><Plus className="w-4 h-4" /> Add a contact</button>
                    </div>
                  )}

                  <button onClick={() => confirm(k)} disabled={busy === k} data-testid={`data-check-confirm-${k}`}
                    className="mt-2 inline-flex items-center gap-2 bg-[#1A3A5F] hover:bg-[#0f2438] text-white text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
                    {busy === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {sec.confirmed ? 'Re-confirm' : `Confirm ${SECTION_META[k].title.toLowerCase()}`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const QuoteBanner = ({ quote, onAccept, isDemo }) => {
  const [busy, setBusy] = useState(false);
  if (!quote) return null;
  const paid = quote.status === 'paid';
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await authApi().post('/supplier-verify/quote/checkout', {});
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout'); setBusy(false); }
  };
  const demoPay = async () => {
    setBusy(true);
    try { await authApi().post('/supplier-verify/quote/demo-pay'); toast.success('Demo payment complete - welcome to your dashboard'); onAccept && onAccept(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Demo pay failed'); setBusy(false); }
  };
  return (
    <div className="border border-[#00A79D]/30 bg-[#E9F7F5] rounded-2xl p-6 mb-6" data-testid="supplier-quote-banner">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#00857D]"><ShieldCheck className="w-3.5 h-3.5" /> {paid ? 'Confirmed-supplier subscription - active' : 'Your confirmed-supplier quote'}</div>
          <h3 className="font-heading text-xl font-bold text-[#1A3A5F] mt-2" data-testid="quote-monthly">{money(quote.monthly_gross)}/month <span className="text-sm font-normal text-gray-500">incl. VAT</span></h3>
          <p className="text-sm text-gray-600 mt-1">Annual contract, paid monthly. Year 1 {money(quote.year1_gross)} incl. VAT (rolls in the one-off market-research piece), then {money(quote.renewal_gross)}/yr.</p>
          <ul className="text-xs text-gray-500 mt-3 space-y-1">
            <li>Subscription: {money(quote.annual_base)} /yr</li>
            <li>Market research (one-off, year 1): {money(quote.market_research_fee)}</li>
            <li>Products ({quote.product_count}): +{money(quote.extra_products_net)} for extra products</li>
          </ul>
        </div>
        <div className="text-right">
          {paid
            ? <span className="inline-flex items-center gap-1.5 text-[#00857D] font-semibold text-sm" data-testid="quote-accepted"><CheckCircle2 className="w-4 h-4" /> Subscription active</span>
            : <div className="flex flex-col items-end gap-2">
                <button onClick={start} disabled={busy} data-testid="quote-accept-btn" className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-5 py-3 rounded-lg disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Accept &amp; pay monthly</button>
                {isDemo && <button onClick={demoPay} disabled={busy} data-testid="quote-demo-pay-btn" className="text-xs font-semibold text-[#00857D] underline">Simulate payment (demo)</button>}
              </div>}
        </div>
      </div>
    </div>
  );
};

export const ResearchReport = ({ productResearch, products }) => {
  const entries = Object.entries(productResearch || {}).filter(([, r]) => r && (r.summary || r.keywords || r.competitors || r.messaging || r.report_url));
  if (entries.length === 0) return null;
  const nameOf = (slug) => (products || []).find((p) => p.slug === slug)?.name || slug;
  const Row = ({ label, val }) => val ? <div className="text-sm"><span className="font-semibold text-[#1A3A5F]">{label}: </span><span className="text-gray-600 whitespace-pre-line">{val}</span></div> : null;
  return (
    <div className="border border-gray-200 bg-white rounded-2xl p-6 mb-6" data-testid="supplier-research-report">
      <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#00857D] mb-3"><ShieldCheck className="w-3.5 h-3.5" /> Your strategic research</div>
      <div className="space-y-4">
        {entries.map(([slug, r]) => (
          <div key={slug} className="border-t border-gray-100 pt-3 first:border-0 first:pt-0" data-testid={`research-product-${slug}`}>
            <p className="font-heading font-semibold text-gray-900 mb-1">{nameOf(slug)}</p>
            <Row label="Summary" val={r.summary} />
            <Row label="Target keywords" val={r.keywords} />
            <Row label="Competitors" val={r.competitors} />
            <Row label="Messaging" val={r.messaging} />
            {r.report_url && <a href={r.report_url} target="_blank" rel="noreferrer" data-testid={`research-report-link-${slug}`} className="inline-flex items-center gap-1.5 text-[#00A79D] font-semibold text-sm mt-1">Download full report →</a>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const PartnerInvite = ({ status, questions, onSubmit }) => {
  const submitted = !!status.partner_questionnaire;
  const pq = status.partner_quote;
  const accepted = pq && pq.status === 'accepted';
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  const acceptQuote = async () => {
    setBusy(true);
    try { await authApi().post('/supplier-verify/partner-quote/accept'); toast.success('Proposal accepted - we\'ll be in touch!'); onSubmit && onSubmit(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not accept'); setBusy(false); }
  };
  const dlPdf = async () => { try { const res = await authApi().get('/supplier-verify/partner-quote/pdf', { responseType: 'blob' }); const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = 'partner-proposal.pdf'; a.click(); } catch { toast.error('Download failed'); } };
  if (submitted) {
    return (
      <div className="border border-[#1A3A5F]/20 bg-[#0f2438] text-white rounded-2xl p-6 mb-6" data-testid="partner-questionnaire-done">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#7FE3DB]"><ShieldCheck className="w-3.5 h-3.5" /> Partner programme</div>
        {pq ? (
          <>
            <h3 className="font-heading text-lg font-bold mt-2" data-testid="partner-quote-view">Your partner-tier proposal</h3>
            <p className="text-sm text-[#C6D5E3] mt-1">{money(pq.monthly_gross)}/month incl. VAT{pq.setup_gross ? ` · ${money(pq.setup_gross)} setup` : ''}</p>
            {pq.scope && <p className="text-sm text-[#E4ECF3] mt-3 whitespace-pre-line">{pq.scope}</p>}
            <div className="flex flex-wrap gap-3 mt-4">
              <button onClick={dlPdf} data-testid="partner-quote-pdf" className="inline-flex items-center gap-2 bg-white text-[#1A3A5F] text-sm font-semibold px-4 py-2 rounded-lg">Download proposal PDF</button>
              {accepted
                ? <span className="inline-flex items-center gap-1.5 text-[#7FE3DB] font-semibold text-sm" data-testid="partner-quote-accepted"><CheckCircle2 className="w-4 h-4" /> Accepted - thank you</span>
                : <button onClick={acceptQuote} disabled={busy} data-testid="partner-quote-accept" className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Accept proposal</button>}
            </div>
          </>
        ) : (
          <>
            <h3 className="font-heading text-lg font-bold mt-2">Thanks - your partner questionnaire is in</h3>
            <p className="text-sm text-[#C6D5E3] mt-1">We'll review your answers and send a tailored partner-tier quote.</p>
          </>
        )}
      </div>
    );
  }
  const submit = async () => {
    setBusy(true);
    try { await authApi().post('/supplier-verify/partner-questionnaire', { answers: ans }); toast.success('Questionnaire submitted'); onSubmit && onSubmit(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); setBusy(false); }
  };
  return (
    <div className="border border-[#1A3A5F]/20 bg-white rounded-2xl p-6 mb-6" data-testid="partner-questionnaire">
      <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#00857D]"><ShieldCheck className="w-3.5 h-3.5" /> You're invited to the partner programme</div>
      <h3 className="font-heading text-lg font-bold text-[#1A3A5F] mt-2">Tell us about your goals</h3>
      <p className="text-sm text-gray-600 mt-1">A few questions so we can scope the partner tier and send you a quote.</p>
      <div className="space-y-3 mt-4">
        {(questions || []).map((q) => (
          <label key={q.key} className="block">
            <span className="text-xs font-semibold text-gray-500">{q.area} · {q.prompt}</span>
            <textarea value={ans[q.key] || ''} onChange={(e) => setAns((a) => ({ ...a, [q.key]: e.target.value }))} rows={2}
              data-testid={`partner-q-${q.key}`} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </label>
        ))}
      </div>
      <button onClick={submit} disabled={busy} data-testid="partner-questionnaire-submit"
        className="mt-4 inline-flex items-center gap-2 bg-[#1A3A5F] hover:bg-[#0f2438] text-white text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit questionnaire
      </button>
    </div>
  );
};
